import { MIN_MAP_WIDTH, DOCK_TAB_WIDTH } from '../theme/layout';

/**
 * Panel budget for the desktop map (docs/plans/panel-system.md, "The hard
 * parts"). Given what is open on each side, decides which docks stay
 * expanded, which collapse to a DOCK_TAB_WIDTH tab, and whether the left
 * dock's companion sits beside its parent or folds into it as a drill-in.
 *
 * `availableWidth` is the content pane the docks overlay (the window minus
 * the 64px rail), not the window, so this never learns about the sidebar.
 *
 * Rules, applied until the map keeps `minMapWidth` and no more than two
 * panels are expanded:
 *   - left group newest: the right dock collapses to a tab first, then the
 *     companion folds into its parent;
 *   - right dock newest: the companion folds first, then the left dock
 *     collapses to a tab (taking its folded companion with it).
 * Nothing ever closes here; that is the user's call.
 */

export type DockSide = 'left' | 'right';
export type DockState = 'expanded' | 'tab';
export type CompanionState = 'beside' | 'folded';

export interface PanelLayoutInput {
  availableWidth: number;
  /** Left dock (plan editor, viewer, layers). Omit when nothing is docked left. */
  left?: { width: number; companionWidth?: number };
  /** Right dock (property, presentation). Omit when nothing is docked right. */
  right?: { width: number };
  /** The side the user touched last. Clicking a tab makes its side newest. */
  newest: DockSide;
  minMapWidth?: number;
}

export interface PanelLayout {
  left: DockState | null;
  right: DockState | null;
  /** null when the left dock has no companion open. */
  companion: CompanionState | null;
  /** Inset each side claims, in px (dock + companion, or the tab). */
  leftInset: number;
  rightInset: number;
  mapWidth: number;
  /** Expanded panels, counting a beside companion as one. */
  expandedCount: number;
}

function measure(
  input: PanelLayoutInput,
  left: DockState | null,
  right: DockState | null,
  companion: CompanionState | null
): PanelLayout {
  const leftInset =
    left === null
      ? 0
      : left === 'tab'
        ? DOCK_TAB_WIDTH
        : input.left!.width + (companion === 'beside' ? (input.left!.companionWidth ?? 0) : 0);
  const rightInset = right === null ? 0 : right === 'tab' ? DOCK_TAB_WIDTH : input.right!.width;
  const expandedCount =
    (left === 'expanded' ? 1 : 0) +
    (left === 'expanded' && companion === 'beside' ? 1 : 0) +
    (right === 'expanded' ? 1 : 0);
  return {
    left,
    right,
    companion,
    leftInset,
    rightInset,
    mapWidth: input.availableWidth - leftInset - rightInset,
    expandedCount,
  };
}

export function resolvePanelLayout(input: PanelLayoutInput): PanelLayout {
  const min = input.minMapWidth ?? MIN_MAP_WIDTH;
  const hasCompanion = !!input.left && (input.left.companionWidth ?? 0) > 0;

  let left: DockState | null = input.left ? 'expanded' : null;
  let right: DockState | null = input.right ? 'expanded' : null;
  let companion: CompanionState | null = hasCompanion ? 'beside' : null;

  const fits = (l: PanelLayout) => l.mapWidth >= min && l.expandedCount <= 2;

  // Each step is one fold, in the order the newest side dictates. A step
  // that does not apply (nothing on that side) is skipped.
  const steps: (() => void)[] =
    input.newest === 'left'
      ? [
          () => {
            if (right === 'expanded') right = 'tab';
          },
          () => {
            if (companion === 'beside') companion = 'folded';
          },
        ]
      : [
          () => {
            if (companion === 'beside') companion = 'folded';
          },
          () => {
            if (left === 'expanded') left = 'tab';
          },
        ];

  let layout = measure(input, left, right, companion);
  for (const step of steps) {
    if (fits(layout)) break;
    step();
    layout = measure(input, left, right, companion);
  }
  return layout;
}
